import { InteractionRequiredAuthError, type AccountInfo } from "@azure/msal-browser";
import { msalInstance } from "../../authClient";
import { loginRequest } from "../../authConfig";
import { createDefaultRsoConstraintLevels, normalizeRsoConstraintLevels } from "../../interfaces/rsoConfiguration";
import { resolveAvantCareEnvHeader } from "../avantCareEnv";
import type { AIEmpowerPayload } from "./AIEmpowerPayloadBuilder";

export interface Credentials {
  baseUrl: string;
}

export type StreamStatusCallback = (message: string) => void;

export const MAX_SUGGESTED_RESOURCES = 4;
export const MAX_GLOBAL_MATCHES = 6;

export interface GlobalMatch {
  resourceId: string;
  matchPercent: number;
  reason: string;
  coveredJobIds?: string[];
  missingJobIds?: string[];
}

export interface GlobalMatchResult {
  matches: GlobalMatch[];
}

export interface IAiAllocation {
  jobId: string;
  resourceId: string;
  start: string;
  end: string;
  reason: string;
  matchPercent: number;
  classification?: string;
  totalCost?: number | null;
  penaltyCost?: number | null;
  penaltyCount?: number;
  penalties?: string[];
}

interface IRsoRunResponse {
  job_id?: string;
}

interface IRsoSolvedResponse {
  status?: string;
  error?: string;
  allocations?: IAiAllocation[];
  candidateAllocations?: IAiAllocation[];
  unassigned?: string[];
}

interface ITimeRange {
  startMs: number;
  endMs: number;
}

interface IRsoCoordinate {
  latitude: number;
  longitude: number;
}

const RSO_POLL_INTERVAL_MS = 1500;
const RSO_WAIT_SECONDS = 1;
const RSO_SCHEMA_VERSION = 2;
const RSO_HARD_CONSTRAINTS = {
  leaveAndNonAvailability: true,
  shiftConflictAvoidance: true,
  availabilityCoverage: true,
  geographicFeasibility: true,
  legalMaxHours: true
} as const;

const getApiCredentials = (): Credentials => ({
  baseUrl: String(process.env.REACT_APP_API_URL ?? "/api").replace(/\/+$/, "")
});

export function setCredentialsFromText(): Credentials {
  return getApiCredentials();
}

export function getCachedCredentials(): Credentials | null {
  return getApiCredentials();
}

export async function readCredentialsFile(): Promise<Credentials> {
  return getApiCredentials();
}

export async function ensureCredentialsInteractive(): Promise<Credentials> {
  return getApiCredentials();
}

const parseRange = (start: unknown, end: unknown): ITimeRange | null => {
  const startMs = new Date(String(start ?? "")).getTime();
  const endMsRaw = new Date(String(end ?? start ?? "")).getTime();
  if (Number.isNaN(startMs)) return null;
  const endMs = Number.isNaN(endMsRaw) ? startMs : endMsRaw;
  if (endMs <= startMs) return null;
  return { startMs, endMs };
};

const hasOverlap = (a: ITimeRange, b: ITimeRange): boolean => a.startMs < b.endMs && a.endMs > b.startMs;

const clampMatchPercent = (value: unknown): number => {
  const asNumber = Number(value);
  if (!Number.isFinite(asNumber)) return 0;
  return Math.max(0, Math.min(100, Math.round(asNumber)));
};

const looksLikeGuid = (value: string): boolean =>
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(value);

const normalizeSkillSet = (skills: unknown): Set<string> => {
  const normalized = new Set<string>();
  if (!Array.isArray(skills)) return normalized;

  skills.forEach((skill: any) => {
    const raw = typeof skill === "string"
      ? skill
      : String(skill?.name ?? skill?.skillName ?? "");
    const clean = raw.trim().toLowerCase();
    if (!clean || looksLikeGuid(clean)) return;
    normalized.add(clean);
  });

  return normalized;
};

const isHumanAssignableResource = (resource: any): boolean => {
  const name = String(resource?.name ?? resource?.title ?? "").trim().toLowerCase();
  if (!name) return false;
  if (name.includes("vehicle")) return false;
  if (name.includes("room")) return false;
  if (name.includes("respite")) return false;
  return true;
};

const toIsoString = (value: unknown, fallback?: string): string => {
  if (typeof value === "string" && value.trim().length > 0) return value;
  if (value instanceof Date && !Number.isNaN(value.getTime())) return value.toISOString();
  return fallback ?? new Date().toISOString();
};

const toFiniteNumber = (value: unknown): number | null => {
  if (value == null || value === "") return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const toNonNegativeNumber = (value: unknown): number | null => {
  const parsed = toFiniteNumber(value);
  if (parsed == null) return null;
  return Math.max(0, parsed);
};

const toTrimmedStringArray = (value: unknown): string[] => {
  if (!Array.isArray(value)) return [];
  return value
    .map((entry) => String(entry ?? "").trim())
    .filter((entry) => entry.length > 0);
};

const toBoolean = (value: unknown): boolean => {
  if (typeof value === "boolean") return value;
  if (typeof value === "number") return value !== 0;
  if (typeof value === "string") {
    const normalized = value.trim().toLowerCase();
    if (!normalized) return false;
    return ["true", "1", "yes", "y"].includes(normalized);
  }
  return false;
};

const mapEmploymentTypeLabel = (value: unknown): string => {
  if (typeof value === "string") {
    const normalized = value.trim().toLowerCase();
    if (normalized) {
      return normalized.replace(/\s+/g, "");
    }
  }
  switch (Number(value)) {
    case 390950000:
      return "fulltime";
    case 390950001:
      return "parttime";
    case 390950002:
      return "casual";
    default:
      return "casual";
  }
};

const normalizeComparableText = (value: unknown): string =>
  String(value ?? "")
    .trim()
    .toLowerCase();

const stripEmployeeLevelSuffix = (value: unknown): string => {
  const text = String(value ?? "").trim();
  return text.replace(/\s*employee level\s*$/i, "").trim();
};

const canonicalizeAwardLevel = (value: unknown): string | null => {
  const text = String(value ?? "").trim();
  if (!text) return null;

  const levelAndPayPointMatch = text.match(/level\s*(\d+)\s*(?:-|–|\.)?\s*(?:pay point\s*)?(\d+)/i);
  if (levelAndPayPointMatch) {
    return `${levelAndPayPointMatch[1]}.${levelAndPayPointMatch[2]}`;
  }

  const dottedLevelMatch = text.match(/(\d+)\.(\d+)/);
  if (dottedLevelMatch) {
    return `${dottedLevelMatch[1]}.${dottedLevelMatch[2]}`;
  }

  return null;
};

const extractAwardMetadata = (resource: any): {
  awardRoleName: string | null;
  awardLevel: string | null;
  awardClassification: string | null;
} => {
  const skills = Array.isArray(resource?.skills) ? resource.skills : [];
  const employeeLevelEntry = skills.find((skill: any) => {
    const ratingModelName = normalizeComparableText(skill?.ratingModelName ?? skill?.ratingModel);
    const skillName = normalizeComparableText(skill?.name ?? skill?.skillName);
    return ratingModelName.includes("employee level") || skillName.includes("employee level");
  });

  if (!employeeLevelEntry) {
    return {
      awardRoleName: null,
      awardLevel: null,
      awardClassification: null
    };
  }

  const classification = String(
    employeeLevelEntry?.rating ??
    employeeLevelEntry?.ratingName ??
    employeeLevelEntry?.classification ??
    ""
  ).trim() || null;

  return {
    awardRoleName: stripEmployeeLevelSuffix(employeeLevelEntry?.ratingModelName ?? employeeLevelEntry?.ratingModel ?? employeeLevelEntry?.name ?? null) || null,
    awardLevel: canonicalizeAwardLevel(classification),
    awardClassification: classification
  };
};

const toRestMinutes = (value: unknown): number => {
  const numeric = toFiniteNumber(value);
  if (numeric == null || numeric <= 0) return 0;
  return numeric <= 24 ? Math.round(numeric * 60) : Math.round(numeric);
};

const mapPayrollVariations = (variations: unknown) => {
  if (!Array.isArray(variations)) return [];
  return variations
    .map((variation: any) => {
      const bookableResourceId = String(variation?.bookableResourceId ?? variation?._illumina_bookableresource_value ?? "").trim();
      const payrollCycleId = String(variation?.payrollCycleId ?? variation?._illumina_payrollcycle_value ?? "").trim();
      if (!bookableResourceId || !payrollCycleId) return null;

      return {
        bookableResourceId,
        payrollCycleId,
        maximumHoursPerPayroll: toFiniteNumber(variation?.maximumHoursPerPayroll ?? variation?.illumina_maximumnoofworkhoursperpayroll) ?? null,
        defaultHoursPerPayroll: toFiniteNumber(variation?.defaultHoursPerPayroll ?? variation?.illumina_defaultnoofworkhoursperpayroll) ?? null,
        minimumHoursPerPayroll: toFiniteNumber(variation?.minimumHoursPerPayroll ?? variation?.illumina_minimumnoofworkhoursperpayroll) ?? null,
        payrollStart: toIsoString(variation?.payrollStart ?? variation?.illumina_payrollcycle?.illumina_startdate ?? null),
        payrollEnd: toIsoString(variation?.payrollEnd ?? variation?.illumina_payrollcycle?.illumina_enddate ?? null)
      };
    })
    .filter((variation) => !!variation);
};

const toCoordinate = (value: any): IRsoCoordinate | null => {
  const latitude = toFiniteNumber(value?.latitude);
  const longitude = toFiniteNumber(value?.longitude);
  if (latitude == null || longitude == null) return null;
  return { latitude, longitude };
};

const toCoordinateTuple = (value: IRsoCoordinate | null | undefined): [number, number] => {
  return [value?.latitude ?? 0, value?.longitude ?? 0];
};

const mergeWindows = (windows: ITimeRange[]): ITimeRange[] => {
  if (!windows.length) return [];
  const ordered = windows
    .slice()
    .sort((a, b) => a.startMs - b.startMs);
  const merged: ITimeRange[] = [ordered[0]];
  for (let index = 1; index < ordered.length; index += 1) {
    const current = ordered[index];
    const last = merged[merged.length - 1];
    if (current.startMs <= last.endMs) {
      last.endMs = Math.max(last.endMs, current.endMs);
      continue;
    }
    merged.push({ ...current });
  }
  return merged;
};

const subtractRanges = (base: ITimeRange[], blocked: ITimeRange[]): ITimeRange[] => {
  let remaining = mergeWindows(base);
  const blockers = mergeWindows(blocked);

  blockers.forEach((block) => {
    const nextRemaining: ITimeRange[] = [];
    remaining.forEach((window) => {
      if (!hasOverlap(window, block)) {
        nextRemaining.push(window);
        return;
      }
      if (block.startMs > window.startMs) {
        nextRemaining.push({ startMs: window.startMs, endMs: Math.min(block.startMs, window.endMs) });
      }
      if (block.endMs < window.endMs) {
        nextRemaining.push({ startMs: Math.max(block.endMs, window.startMs), endMs: window.endMs });
      }
    });
    remaining = nextRemaining.filter((window) => window.endMs > window.startMs);
  });

  return remaining;
};

const deriveResourceShiftRanges = (resource: any, planningWindow: { start: string; end: string }): ITimeRange[] => {
  const planningRange = parseRange(planningWindow.start, planningWindow.end);
  if (!planningRange) return [];

  const baseWindows = [
    ...(Array.isArray(resource?.availability?.available) ? resource.availability.available : []),
    ...(Array.isArray(resource?.availability?.preferred) ? resource.availability.preferred : []),
    ...(Array.isArray(resource?.availability?.contracted) ? resource.availability.contracted : [])
  ]
    .map((window: any) => parseRange(window?.start, window?.end))
    .filter((window): window is ITimeRange => !!window)
    .map((window) => ({
      startMs: Math.max(window.startMs, planningRange.startMs),
      endMs: Math.min(window.endMs, planningRange.endMs)
    }))
    .filter((window) => window.endMs > window.startMs);

  const effectiveBase = baseWindows.length > 0 ? baseWindows : [planningRange];
  const blocked = [
    ...(Array.isArray(resource?.availability?.unavailable) ? resource.availability.unavailable : []),
    ...(Array.isArray(resource?.approvedLeaves) ? resource.approvedLeaves : [])
  ]
    .map((window: any) => parseRange(window?.start, window?.end))
    .filter((window): window is ITimeRange => !!window);

  return subtractRanges(effectiveBase, blocked);
};

const buildRsoPayload = (payload: AIEmpowerPayload) => {
  const siteBaseLocation = toCoordinate(payload.siteBaseLocation) ?? { latitude: 0, longitude: 0 };
  const optimisationGoals = normalizeRsoConstraintLevels(payload.rsoConstraintLevels ?? createDefaultRsoConstraintLevels());

  const mapWindows = (windows: Array<{ start?: string | Date | null; end?: string | Date | null }> | undefined) =>
    (windows ?? [])
      .map((window, index) => ({
        id: `window-${index + 1}`,
        start: toIsoString(window?.start),
        end: toIsoString(window?.end, toIsoString(window?.start))
      }))
      .filter((window) => !!window.start && !!window.end);

  const nurses = (payload.resources ?? [])
    .filter((resource: any) => isHumanAssignableResource(resource))
    .map((resource: any) => {
      const shiftRanges = deriveResourceShiftRanges(resource, payload.planningWindow);
      if (shiftRanges.length === 0) return null;

      const { awardRoleName, awardLevel, awardClassification } = extractAwardMetadata(resource);
      const homeLocation = toCoordinate(resource?.resourceAddressCoordinates) ?? siteBaseLocation;
      const availability = resource?.availability ?? {};
      const contractedWindows = mapWindows(resource?.contractedHours ?? availability.contracted);
      const availableWindows = mapWindows(availability.available);
      const preferredWindows = mapWindows(availability.preferred);
      const unavailableWindows = mapWindows(availability.unavailable);
      const skillDetails = (resource?.skills ?? [])
        .map((skill: any) => ({
          skillName: String(skill?.name ?? skill?.skillName ?? "").trim(),
          rating: String(skill?.rating ?? skill?.ratingName ?? "").trim() || null,
          expiryDate: skill?.expiryDate ? toIsoString(skill.expiryDate) : null
        }))
        .filter((skill: any) => !!skill.skillName);
      const payrollHourVariations = mapPayrollVariations(resource?.payrollHourVariations);
      const minimumBreakBetweenShifts = Number(resource?.minimumBreakBetweenShifts ?? 0) || 0;
      const maximumWorkingHoursPerDay = Number(resource?.maximumWorkingHoursPerDay ?? resource?.maxHoursPerDay ?? 0) || 0;

      return {
        id: String(resource?.id ?? ""),
        name: String(resource?.name ?? resource?.title ?? resource?.id ?? ""),
        employmentType: mapEmploymentTypeLabel(resource?.currentEmploymentType ?? resource?.employmentType ?? "casual"),
        currentEmploymentType: toFiniteNumber(resource?.currentEmploymentType ?? null),
        applicableAward: toFiniteNumber(resource?.applicableAward ?? null),
        classification: "rostering",
        baseRateAUD: Number(resource?.baseRateAUD ?? resource?.hourlyRate ?? resource?.ratePerHour ?? 1) || 1,
        awardRoleName,
        awardLevel,
        awardClassification,
        homeLocation: toCoordinateTuple(homeLocation),
        contractedHoursPerCycle: Number(resource?.minimumWorkHoursPerPayroll ?? 0) || 0,
        minimumWorkHoursPerPayroll: Number(resource?.minimumWorkHoursPerPayroll ?? 0) || 0,
        defaultWorkHoursPerPayroll: Number(resource?.defaultWorkHoursPerPayroll ?? resource?.minimumWorkHoursPerPayroll ?? 0) || 0,
        maxHoursPerShift: Number(resource?.maxHoursPerShift ?? 0) || 0,
        maxHoursPerDay: maximumWorkingHoursPerDay || 0,
        maxHoursPerPayrun: Number(resource?.maximumWorkHourPerPayroll ?? 80) || 80,
        maximumWorkingHoursPerDay,
        minimumBreakBetweenShifts,
        restBetweenShiftsMinutes: toRestMinutes(resource?.minimumBreakBetweenShifts),
        countPassiveSleepOverAsBreak: toBoolean(resource?.countPassiveSleepOverAsBreak),
        payrollHourVariations,
        shiftPreferences: ["any"],
        skills: Array.from(normalizeSkillSet(resource?.skills ?? [])).map((skillName) => ({ skillName })),
        skillDetails,
        availability: {
          available: availableWindows,
          preferred: preferredWindows,
          contracted: contractedWindows,
          unavailable: unavailableWindows
        },
        approvedLeaves: mapWindows(resource?.approvedLeaves),
        travelAnchor: {
          startLocationType: resource?.startLocationType ?? null,
          endLocationType: resource?.endLocationType ?? null,
          startLocationLabel: resource?.startLocationLabel ?? null,
          endLocationLabel: resource?.endLocationLabel ?? null,
          resourceAddressCoordinates: resource?.resourceAddressCoordinates ?? null,
          resourceAddressLabel: resource?.resourceAddressLabel ?? null
        },
        categories: Array.isArray(resource?.categories)
          ? resource.categories.map((category: any) => String(category?.name ?? category ?? "").trim()).filter(Boolean)
          : [],
        siteLocations: Array.isArray(resource?.siteLocations)
          ? resource.siteLocations.map((site: any) => String(site?.name ?? site ?? "").trim()).filter(Boolean)
          : [],
        personalNote: resource?.personalNote ?? null,
        minimumShiftHours: 1,
        mealBreakTriggerHoursWorked: 5,
        mealBreakDurationMinutes: 30,
        restBreakFrequencyHoursWorked: 4,
        restBreakDurationMinutes: 10,
        shifts: shiftRanges.map((shift, index) => ({
          id: `${resource?.id ?? "resource"}-shift-${index + 1}`,
          date: new Date(shift.startMs).toISOString().slice(0, 10),
          startLocation: toCoordinateTuple(homeLocation),
          minStartTime: new Date(shift.startMs).toISOString(),
          maxEndTime: new Date(shift.endMs).toISOString(),
          availableWindows,
          preferredWindows,
          contractedWindows
        }))
      };
    })
    .filter((resource): resource is NonNullable<typeof resource> => !!resource && !!resource.id);

  const visits = (payload.unassignedJobs ?? []).map((job: any) => {
    const location = toCoordinate(job?.siteCoordinates) ?? siteBaseLocation;
    const start = toIsoString(job?.start, payload.planningWindow.start);
    const end = toIsoString(job?.end, payload.planningWindow.end);
      return {
      id: String(job?.jobId ?? ""),
      appointmentLocation: toCoordinateTuple(location),
      serviceDuration: String(job?.durationMinutes ?? 0),
      durationMinutes: Number(job?.durationMinutes ?? 0) || 0,
      hourlyRate: job?.hourlyRate ?? null,
      estimatedTravelDuration: job?.estimatedTravelDuration ?? null,
      products: Array.isArray(job?.products) ? job.products : [],
      services: Array.isArray(job?.services) ? job.services : [],
      timeWindows: [
        {
          minStartTime: start,
          maxEndTime: end
        }
      ],
      requiredSkillsMandatory: Array.from(normalizeSkillSet(job?.requiredSkills ?? [])),
      requiredSkillsOptional: [],
      continuityPreference: null,
      setupMinutes: 0,
      priority: job?.priority == null ? null : String(job.priority),
      optionalVisit: false,
      dependencies: [],
      title: String(job?.title ?? job?.jobId ?? ""),
      preferredResourceId: job?.preferredResourceId ?? null,
      preferredResourceName: job?.preferredResourceName ?? null,
      isJobBidding: !!job?.isJobBidding,
      jobType: job?.jobType ?? null,
      workOrderType: job?.workOrderType ?? null,
      workOrderTypeId: job?.workOrderTypeId ?? null,
      displayCost: job?.displayCost ?? null,
      actualCost: job?.actualCost ?? null,
      siteLocation: job?.siteLocation ?? null,
      siteLocationDetails: job?.siteLocationDetails ?? null,
      serviceAccount: job?.serviceAccount ?? null
    };
  }).filter((visit: any) => !!visit.id);

  const bookings = (payload.bookings ?? []).map((booking: any) => {
    const location = toCoordinate(booking?.siteCoordinates) ?? siteBaseLocation;
    return {
      id: String(booking?.bookingId ?? booking?.jobId ?? ""),
      resourceId: String(booking?.resourceId ?? ""),
      appointmentLocation: toCoordinateTuple(location),
      start: toIsoString(booking?.start),
      end: toIsoString(booking?.end, booking?.start),
      requiredSkillsMandatory: Array.from(normalizeSkillSet(booking?.requiredSkills ?? [])),
      title: String(booking?.title ?? booking?.jobId ?? ""),
      preferredResourceId: booking?.preferredResourceId ?? null,
      preferredResourceName: booking?.preferredResourceName ?? null,
      isJobBidding: !!booking?.isJobBidding,
      jobType: booking?.jobType ?? null,
      workOrderType: booking?.workOrderType ?? null,
      workOrderTypeId: booking?.workOrderTypeId ?? null,
      hourlyRate: booking?.hourlyRate ?? null,
      estimatedTravelDuration: booking?.estimatedTravelDuration ?? null,
      products: Array.isArray(booking?.products) ? booking.products : [],
      services: Array.isArray(booking?.services) ? booking.services : [],
      displayCost: booking?.displayCost ?? null,
      actualCost: booking?.actualCost ?? null,
      siteLocation: booking?.siteLocation ?? null,
      siteLocationDetails: booking?.siteLocationDetails ?? null,
      serviceAccount: booking?.serviceAccount ?? null,
      durationMinutes: Number(booking?.durationMinutes ?? 0) || 0,
      shiftLengthMinutes: Number(booking?.shiftLengthMinutes ?? booking?.durationMinutes ?? 0) || 0
    };
  }).filter((booking: any) => !!booking.id && !!booking.resourceId);

  return {
    nurses,
    visits,
    bookings,
    config: {
      schemaVersion: RSO_SCHEMA_VERSION,
      source: "Rosterboard",
      hardConstraints: RSO_HARD_CONSTRAINTS,
      optimisationGoals
    }
  };
};

const sleep = async (ms: number, signal?: AbortSignal): Promise<void> => {
  await new Promise<void>((resolve, reject) => {
    const timeout = window.setTimeout(() => {
      if (signal) {
        signal.removeEventListener("abort", onAbort);
      }
      resolve();
    }, ms);

    const onAbort = () => {
      window.clearTimeout(timeout);
      reject(new DOMException("Request aborted", "AbortError"));
    };

    if (signal) {
      if (signal.aborted) {
        onAbort();
        return;
      }
      signal.addEventListener("abort", onAbort, { once: true });
    }
  });
};

const getActiveAccount = (): AccountInfo | null => {
  const active = msalInstance.getActiveAccount();
  if (active) return active;
  const accounts = msalInstance.getAllAccounts();
  return accounts.length > 0 ? accounts[0] : null;
};

const getAvantCareEnvHeader = (): string => resolveAvantCareEnvHeader();

const acquireApiToken = async (): Promise<string> => {
  const account = getActiveAccount();
  if (!account) {
    throw new Error("No active Microsoft account found for the Rostering API request.");
  }

  const request = {
    ...loginRequest,
    account
  };

  try {
    const response = await msalInstance.acquireTokenSilent(request);
    return response.accessToken;
  } catch (error) {
    if (error instanceof InteractionRequiredAuthError) {
      await msalInstance.acquireTokenRedirect(request);
    }
    throw error;
  }
};

const runRsoJob = async (payload: AIEmpowerPayload, signal?: AbortSignal): Promise<IRsoRunResponse> => {
  const creds = getApiCredentials();
  const token = await acquireApiToken();
  const rsoPayload = buildRsoPayload(payload);

  const response = await fetch(`${creds.baseUrl}/resourceoptimizer/run?debugTimeoutSeconds=40`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
      "X-AvantCare-Env": getAvantCareEnvHeader()
    },
    body: JSON.stringify(rsoPayload),
    signal
  });

  if (!response.ok) {
    const text = await response.text().catch(() => "");
    throw new Error(`RSO request failed (${response.status}): ${text}`);
  }

  return response.json();
};

const getSolvedJob = async (jobId: string, signal?: AbortSignal): Promise<IRsoSolvedResponse> => {
  const creds = getApiCredentials();
  const token = await acquireApiToken();
  const response = await fetch(`${creds.baseUrl}/resourceoptimizer/solved/${jobId}?waitSeconds=${RSO_WAIT_SECONDS}`, {
    method: "GET",
    headers: {
      Authorization: `Bearer ${token}`,
      "X-AvantCare-Env": getAvantCareEnvHeader()
    },
    signal
  });

  if (!response.ok) {
    const text = await response.text().catch(() => "");
    throw new Error(`RSO polling failed (${response.status}): ${text}`);
  }

  return response.json();
};

const buildBusyRangesByResource = (payload: AIEmpowerPayload, existingAllocations: IAiAllocation[]) => {
  const busyRangesByResource = new Map<string, ITimeRange[]>();
  const addBusyRange = (resourceId: string, range: ITimeRange) => {
    const existing = busyRangesByResource.get(resourceId) ?? [];
    existing.push(range);
    busyRangesByResource.set(resourceId, existing);
  };

  (payload.bookings ?? []).forEach((booking: any) => {
    const resourceId = String(booking?.resourceId ?? "");
    if (!resourceId) return;
    const range = parseRange(booking?.start, booking?.end ?? booking?.start);
    if (!range) return;
    addBusyRange(resourceId, range);
  });

  (payload.resources ?? []).forEach((resource: any) => {
    const resourceId = String(resource?.id ?? "");
    if (!resourceId) return;
    (resource?.approvedLeaves ?? []).forEach((leave: any) => {
      const range = parseRange(leave?.start, leave?.end);
      if (!range) return;
      addBusyRange(resourceId, range);
    });
  });

  existingAllocations.forEach((allocation) => {
    const range = parseRange(allocation.start, allocation.end);
    if (!range) return;
    addBusyRange(allocation.resourceId, range);
  });

  return { busyRangesByResource, addBusyRange };
};

const estimateFallbackBaseRate = (resource: any): number | null =>
  toNonNegativeNumber(resource?.baseRateAUD ?? resource?.hourlyRate ?? resource?.ratePerHour);

const estimateFallbackPenaltyCost = (resource: any, jobRange: ITimeRange, baseRate: number): number => {
  const minimumShiftHours = toNonNegativeNumber(resource?.minimumShiftHours);
  if (minimumShiftHours == null || minimumShiftHours <= 0) {
    return 0;
  }

  const assignmentHours = Math.max(0, (jobRange.endMs - jobRange.startMs) / 3_600_000);
  if (assignmentHours >= minimumShiftHours) {
    return 0;
  }

  return Math.round((minimumShiftHours - assignmentHours) * baseRate * 100) / 100;
};

const estimateFallbackCosts = (
  resource: any,
  jobRange: ITimeRange
): { totalCost: number | null; penaltyCost: number | null } => {
  const baseRate = estimateFallbackBaseRate(resource);
  if (baseRate == null) {
    return { totalCost: null, penaltyCost: null };
  }

  const assignmentHours = Math.max(0, (jobRange.endMs - jobRange.startMs) / 3_600_000);
  const labourCost = Math.round(assignmentHours * baseRate * 100) / 100;
  const penaltyCost = estimateFallbackPenaltyCost(resource, jobRange, baseRate);

  return {
    totalCost: Math.round((labourCost + penaltyCost) * 100) / 100,
    penaltyCost
  };
};

const padAllocationsWithFallback = (
  payload: AIEmpowerPayload,
  allocations: IAiAllocation[],
  maxPerJob = MAX_SUGGESTED_RESOURCES
): IAiAllocation[] => {
  const jobRanges = new Map<string, ITimeRange>();
  const jobById = new Map<string, any>();
  (payload.unassignedJobs ?? []).forEach((job: any) => {
    const jobId = String(job?.jobId ?? "");
    if (!jobId) return;
    const range = parseRange(job?.start, job?.end);
    if (!range) return;
    jobRanges.set(jobId, range);
    jobById.set(jobId, job);
  });

  const candidateMapByJob = new Map<string, IAiAllocation[]>();
  allocations.forEach((allocation) => {
    const jobId = String(allocation?.jobId ?? "");
    const resourceId = String(allocation?.resourceId ?? "");
    if (!jobId || !resourceId) return;
    const existing = candidateMapByJob.get(jobId) ?? [];
    const duplicateIndex = existing.findIndex((entry) => entry.resourceId === resourceId);
    if (duplicateIndex > -1) {
      if (existing[duplicateIndex].matchPercent >= allocation.matchPercent) return;
      existing[duplicateIndex] = allocation;
      candidateMapByJob.set(jobId, existing);
      return;
    }
    existing.push(allocation);
    candidateMapByJob.set(jobId, existing);
  });

  const { busyRangesByResource } = buildBusyRangesByResource(payload, allocations);

  Array.from(jobRanges.entries()).forEach(([jobId, jobRange]) => {
    const existingCandidates = candidateMapByJob.get(jobId) ?? [];
    if (existingCandidates.length >= maxPerJob) return;

    const existingIds = new Set(existingCandidates.map((candidate) => candidate.resourceId));
    const job = jobById.get(jobId);
    const requiredSkills = normalizeSkillSet(job?.requiredSkills);
    const fallbackCandidates: IAiAllocation[] = [];

    (payload.resources ?? []).forEach((resource: any) => {
      const resourceId = String(resource?.id ?? "");
      if (!resourceId || existingIds.has(resourceId)) return;
      if (!isHumanAssignableResource(resource)) return;

      const shiftRanges = deriveResourceShiftRanges(resource, payload.planningWindow);
      const isWithinShift = shiftRanges.some((shiftRange) =>
        jobRange.startMs >= shiftRange.startMs && jobRange.endMs <= shiftRange.endMs
      );
      if (!isWithinShift) return;

      const busyRanges = busyRangesByResource.get(resourceId) ?? [];
      const isBusy = busyRanges.some((busyRange) => hasOverlap(busyRange, jobRange));
      if (isBusy) return;

      const resourceSkills = normalizeSkillSet(resource?.skills ?? []);
      let matchedSkills = 0;
      if (requiredSkills.size > 0) {
        requiredSkills.forEach((skill) => {
          if (resourceSkills.has(skill)) matchedSkills += 1;
        });
      }

      const skillRatio = requiredSkills.size > 0 ? matchedSkills / requiredSkills.size : 0;
      const matchPercent = requiredSkills.size > 0
        ? clampMatchPercent(48 + Math.round(skillRatio * 22))
        : 60;
      const reason = requiredSkills.size === 0
        ? "Fallback suggestion: available in the timeframe with no overlapping bookings or leave."
        : matchedSkills > 0
          ? `Fallback suggestion: available in the timeframe with ${matchedSkills}/${requiredSkills.size} required skills matched.`
          : "Fallback suggestion: available in the timeframe; required skills could not be confirmed from the payload.";
      const { totalCost, penaltyCost } = estimateFallbackCosts(resource, jobRange);

      fallbackCandidates.push({
        jobId,
        resourceId,
        start: new Date(jobRange.startMs).toISOString(),
        end: new Date(jobRange.endMs).toISOString(),
        reason,
        matchPercent,
        classification: "fallback",
        totalCost,
        penaltyCost,
        penaltyCount: penaltyCost != null && penaltyCost > 0 ? 1 : 0,
        penalties: []
      });
    });

    fallbackCandidates
      .sort((a, b) => b.matchPercent - a.matchPercent)
      .slice(0, Math.max(0, maxPerJob - existingCandidates.length))
      .forEach((candidate) => existingCandidates.push(candidate));

    candidateMapByJob.set(jobId, existingCandidates);
  });

  const padded: IAiAllocation[] = [];
  Array.from(candidateMapByJob.keys()).forEach((jobId) => {
    const candidates = (candidateMapByJob.get(jobId) ?? [])
      .slice()
      .sort((a, b) => b.matchPercent - a.matchPercent)
      .slice(0, maxPerJob);
    padded.push(...candidates);
  });

  return padded;
};

const validateAllocations = (allocations: IAiAllocation[]) => {
  const seenByJob = new Map<string, Set<string>>();
  allocations.forEach((allocation) => {
    if (!allocation.reason || !allocation.reason.trim()) {
      throw new Error("Missing reason in one or more allocations");
    }
    if (!Number.isInteger(allocation.matchPercent) || allocation.matchPercent < 0 || allocation.matchPercent > 100) {
      throw new Error("Invalid matchPercent (expected integer 0-100) in one or more allocations");
    }

    const seen = seenByJob.get(allocation.jobId) ?? new Set<string>();
    if (seen.has(allocation.resourceId)) {
      throw new Error(`Duplicate resourceId ${allocation.resourceId} for job ${allocation.jobId}`);
    }
    seen.add(allocation.resourceId);
    seenByJob.set(allocation.jobId, seen);
  });
};

const normalizeRsoAllocations = (
  payload: AIEmpowerPayload,
  solved: IRsoSolvedResponse,
  options?: { preferCandidateSuggestions?: boolean }
): { allocations: IAiAllocation[]; unassigned: string[] } => {
  const preferredAllocations =
    options?.preferCandidateSuggestions && Array.isArray(solved?.candidateAllocations) && solved.candidateAllocations.length > 0
      ? solved.candidateAllocations
      : solved?.allocations;
  const rawAllocations = Array.isArray(preferredAllocations) ? preferredAllocations : [];
  const normalized = rawAllocations
    .map((allocation) => ({
      jobId: String(allocation?.jobId ?? ""),
      resourceId: String(allocation?.resourceId ?? ""),
      start: toIsoString(allocation?.start),
      end: toIsoString(allocation?.end, allocation?.start),
      reason: String(allocation?.reason ?? "").trim() || "Suggested by the resource optimizer.",
      matchPercent: clampMatchPercent(allocation?.matchPercent),
      classification: String(allocation?.classification ?? "").trim() || undefined,
      totalCost: toNonNegativeNumber(allocation?.totalCost),
      penaltyCost: toNonNegativeNumber(allocation?.penaltyCost),
      penaltyCount: Math.max(
        0,
        Math.round(
          toFiniteNumber(allocation?.penaltyCount) ??
          toTrimmedStringArray(allocation?.penalties).length
        )
      ),
      penalties: toTrimmedStringArray(allocation?.penalties)
    }))
    .filter((allocation) => allocation.jobId && allocation.resourceId);

  const padded = padAllocationsWithFallback(payload, normalized);
  validateAllocations(padded);
  return {
    allocations: padded,
    unassigned: Array.isArray(solved?.unassigned) ? solved.unassigned.map((jobId) => String(jobId)) : []
  };
};

const runAndNormalizeRsoRequest = async (
  payload: AIEmpowerPayload,
  onStatus?: StreamStatusCallback,
  signal?: AbortSignal,
  options?: { preferCandidateSuggestions?: boolean }
): Promise<{ allocations: IAiAllocation[]; unassigned: string[] }> => {
  onStatus?.("Submitting optimization request...");
  const queued = await runRsoJob(payload, signal);
  const jobId = String(queued?.job_id ?? "").trim();
  if (!jobId) {
    throw new Error("RSO did not return a job id.");
  }

  onStatus?.("Optimization queued...");
  while (true) {
    if (signal?.aborted) {
      throw new DOMException("Request aborted", "AbortError");
    }

    const solved = await getSolvedJob(jobId, signal);
    const status = String(solved?.status ?? "").toLowerCase();
    if (status === "succeeded") {
      onStatus?.("Matches ready.");
      return normalizeRsoAllocations(payload, solved, options);
    }
    if (status === "failed" || status === "timeout") {
      throw new Error(String(solved?.error ?? "RSO optimization failed."));
    }

    onStatus?.("Optimizing...");
    await sleep(RSO_POLL_INTERVAL_MS, signal);
  }
};

export async function sendChatGPTRequest(payload: AIEmpowerPayload): Promise<any> {
  return runAndNormalizeRsoRequest(payload);
}

export async function sendChatGPTGlobalMatchRequest(
  payload: AIEmpowerPayload,
  maxMatches = MAX_GLOBAL_MATCHES
): Promise<GlobalMatchResult> {
  const result = await runAndNormalizeRsoRequest(payload);
  const totalJobs = new Set((payload.unassignedJobs ?? []).map((job: any) => String(job?.jobId ?? "")));
  const coverageByResource = new Map<string, IAiAllocation[]>();

  result.allocations.forEach((allocation) => {
    const existing = coverageByResource.get(allocation.resourceId) ?? [];
    existing.push(allocation);
    coverageByResource.set(allocation.resourceId, existing);
  });

  const matches = Array.from(coverageByResource.entries())
    .map(([resourceId, allocations]) => {
      const coveredJobIds = Array.from(new Set(allocations.map((allocation) => allocation.jobId)));
      const missingJobIds = Array.from(totalJobs).filter((jobId) => !coveredJobIds.includes(jobId));
      const averageScore = allocations.length > 0
        ? Math.round(allocations.reduce((total, allocation) => total + allocation.matchPercent, 0) / allocations.length)
        : 0;

      return {
        resourceId,
        matchPercent: clampMatchPercent(averageScore - missingJobIds.length * 8),
        reason: missingJobIds.length === 0
          ? "Can cover all targeted jobs in the current optimization result."
          : `Covers ${coveredJobIds.length} of ${totalJobs.size} targeted jobs.`,
        coveredJobIds,
        missingJobIds
      };
    })
    .sort((a, b) => b.matchPercent - a.matchPercent)
    .slice(0, maxMatches);

  return { matches };
}

export async function streamChatGPTRequest(
  payload: AIEmpowerPayload,
  onStatus?: StreamStatusCallback,
  signal?: AbortSignal,
  options?: { preferCandidateSuggestions?: boolean }
): Promise<any> {
  return runAndNormalizeRsoRequest(payload, onStatus, signal, options);
}

export async function optimizeWithChatGPT(payload: AIEmpowerPayload): Promise<any> {
  return sendChatGPTRequest(payload);
}

export { buildRsoPayload };

export default optimizeWithChatGPT;
